import { useCallback } from 'react';
import { useAppData } from '../contexts/AppDataContext';
import { addBusinessDays, addCalendarDays, daysBetween } from '../../lib/util';
import { STRIKE_THRESHOLDS } from '../../domain/constants';
import { newIdempotencyKey } from '../../lib/idempotency';
import {
  openShaftDispute,
  patchShaftDispute,
  openIpDispute,
  patchIpDispute,
  type ShaftDispute,
  type IpDispute,
} from '../../api/disputes';

export interface DisputeActions {
  createShaftDisputeForCase: (caseId: string, contentType: string, details: string) => void;
  notifyRegistrantShaft: (caseId: string, disputeId: string) => void;
  respondShaftDispute: (caseId: string, disputeId: string, responseType: string, content: string) => void;
  resolveShaftDispute: (caseId: string, disputeId: string, outcome: string) => void;
  createIpDisputeForCase: (caseId: string, type: string, claimantName: string, claimDetails: string, trademarkInfo?: any) => void;
  notifyRegistrantIp: (caseId: string, disputeId: string) => void;
  suspendIpContent: (caseId: string, disputeId: string) => void;
  respondIpDispute: (caseId: string, disputeId: string, counterClaimDetails: string) => void;
  resolveIpDispute: (caseId: string, disputeId: string, outcome: string) => void;
}

export function useDisputeActions(): DisputeActions {
  const { cases, mergeLocalCase, showToast, updateCase } = useAppData();

  // #623 — the two NOTIFY deadlines are NOT sent from here. The server stamps
  // `oa_notify_deadline` / `notify_deadline` when it records the dispute
  // (apps/api/src/routes/disputes.ts) and leaves them out of the PATCH
  // allowlists, so the value the DeadlineIndicator renders arrives on the
  // response row that mergeLocalCase folds in below. What the notify actions
  // DO write is `registrantResponseDeadline` — the registrant's clock, a
  // different obligation with a different term (90 calendar days for SHAFT, 7
  // for IP). Sending a deadline from the browser would put it on the analyst's
  // local clock, which is what #286 moved the vetting SLA away from.
  //
  // Each dispute mutation hits a dedicated server endpoint (POST to open,
  // PATCH to transition), then folds the canonical row back into the local
  // case via mergeLocalCase. Going through updateCase / PATCH /cases would
  // silently drop the dispute arrays — shaft_disputes / ip_disputes are
  // separate tables, not case columns.
  const appendShaft = useCallback((caseId: string, dispute: ShaftDispute) => {
    const c = cases.find(x => x.id === caseId);
    if (!c) return;
    const existing = (c.shaftDisputes ?? []) as ShaftDispute[];
    mergeLocalCase(caseId, { shaftDisputes: [...existing.filter(d => d.id !== dispute.id), dispute] } as any);
  }, [cases, mergeLocalCase]);

  const appendIp = useCallback((caseId: string, dispute: IpDispute) => {
    const c = cases.find(x => x.id === caseId);
    if (!c) return;
    const existing = (c.ipDisputes ?? []) as IpDispute[];
    mergeLocalCase(caseId, { ipDisputes: [...existing.filter(d => d.id !== dispute.id), dispute] } as any);
  }, [cases, mergeLocalCase]);

  const createShaftDisputeForCase = useCallback((caseId: string, contentType: string, details: string) => {
    void openShaftDispute(
      caseId,
      { contentType, details, action: { code: 'SHAFT_DISPUTE_CREATED', detail: { contentType, details } } },
      newIdempotencyKey(),
    )
      .then(d => appendShaft(caseId, d))
      .catch(err => showToast(`Failed to flag SHAFT content: ${err instanceof Error ? err.message : String(err)}`));
  }, [appendShaft, showToast]);

  const notifyRegistrantShaft = useCallback((caseId: string, disputeId: string) => {
    const now2 = new Date().toISOString();
    void patchShaftDispute(caseId, disputeId, {
      status: 'NOTIFIED',
      oaNotifiedRegistrantAt: now2,
      registrantResponseDeadline: addCalendarDays(now2, 90).toISOString(),
      action: { code: 'SHAFT_REGISTRANT_NOTIFIED', detail: { disputeId, responseDays: 90 } },
    })
      .then(d => appendShaft(caseId, d))
      .catch(err => showToast(`Failed to notify registrant: ${err instanceof Error ? err.message : String(err)}`));
  }, [appendShaft, showToast]);

  const respondShaftDispute = useCallback((caseId: string, disputeId: string, responseType: string, content: string) => {
    const now2 = new Date().toISOString();
    void patchShaftDispute(caseId, disputeId, {
      status: responseType === 'MODIFICATION' ? 'MODIFICATION_SUBMITTED' : 'OBJECTION_SUBMITTED',
      registrantResponse: responseType,
      registrantResponseAt: now2,
      modificationContent: content || '',
      bcidReviewDeadline: responseType === 'OBJECTION' ? addCalendarDays(now2, 30).toISOString() : null,
      action: { code: responseType === 'MODIFICATION' ? 'SHAFT_MODIFICATION' : 'SHAFT_OBJECTION', detail: { disputeId, responseType } },
    })
      .then(d => appendShaft(caseId, d))
      .catch(err => showToast(`Failed to record response: ${err instanceof Error ? err.message : String(err)}`));
  }, [appendShaft, showToast]);

  const resolveShaftDispute = useCallback((caseId: string, disputeId: string, outcome: string) => {
    const now2 = new Date().toISOString();
    void patchShaftDispute(caseId, disputeId, {
      status: outcome,
      finalDetermination: outcome,
      finalDeterminationAt: now2,
      action: { code: 'SHAFT_RESOLVED', detail: { disputeId, outcome } },
    })
      .then(d => appendShaft(caseId, d))
      .catch(err => showToast(`Failed to resolve dispute: ${err instanceof Error ? err.message : String(err)}`));
  }, [appendShaft, showToast]);

  const createIpDisputeForCase = useCallback((caseId: string, type: string, claimantName: string, claimDetails: string, trademarkInfo?: any) => {
    void openIpDispute(
      caseId,
      {
        type,
        claimantName,
        claimDetails,
        action: { code: 'IP_DISPUTE_RECEIVED', detail: { type, claimantName } },
        ...(trademarkInfo ?? {}),
      },
      newIdempotencyKey(),
    )
      .then(d => appendIp(caseId, d))
      .catch(err => showToast(`Failed to file claim: ${err instanceof Error ? err.message : String(err)}`));
  }, [appendIp, showToast]);

  const notifyRegistrantIp = useCallback((caseId: string, disputeId: string) => {
    const now2 = new Date().toISOString();
    void patchIpDispute(caseId, disputeId, {
      status: 'REGISTRANT_NOTIFIED',
      registrantNotifiedAt: now2,
      registrantResponseDeadline: addCalendarDays(now2, 7).toISOString(),
      action: { code: 'IP_REGISTRANT_NOTIFIED', detail: { disputeId, responseDays: 7 } },
    })
      .then(d => appendIp(caseId, d))
      .catch(err => showToast(`Failed to notify registrant: ${err instanceof Error ? err.message : String(err)}`));
  }, [appendIp, showToast]);

  const suspendIpContent = useCallback((caseId: string, disputeId: string) => {
    const now2 = new Date().toISOString();
    void patchIpDispute(caseId, disputeId, {
      status: 'SUSPENDED',
      contentSuspendedAt: now2,
      action: { code: 'IP_CONTENT_SUSPENDED', detail: { disputeId } },
    })
      .then(d => appendIp(caseId, d))
      .catch(err => showToast(`Failed to suspend content: ${err instanceof Error ? err.message : String(err)}`));
  }, [appendIp, showToast]);

  const respondIpDispute = useCallback((caseId: string, disputeId: string, counterClaimDetails: string) => {
    const c = cases.find(x => x.id === caseId);
    const existing = (c?.ipDisputes ?? []) as IpDispute[];
    const dispute = existing.find(d => d.id === disputeId);
    const now2 = new Date().toISOString();
    void patchIpDispute(caseId, disputeId, {
      status: 'CONTESTED',
      registrantResponse: 'COUNTER_CLAIM',
      registrantResponseAt: now2,
      counterClaimDetails,
      reinstateEarliestDate: dispute?.type === 'COPYRIGHT' ? addBusinessDays(now2, 10).toISOString() : null,
      reinstateLatestDate:   dispute?.type === 'COPYRIGHT' ? addBusinessDays(now2, 14).toISOString() : null,
      action: { code: 'IP_COUNTER_CLAIM', detail: { disputeId, type: dispute?.type } },
    })
      .then(d => appendIp(caseId, d))
      .catch(err => showToast(`Failed to record counter-claim: ${err instanceof Error ? err.message : String(err)}`));
  }, [cases, appendIp, showToast]);

  const resolveIpDispute = useCallback((caseId: string, disputeId: string, outcome: string) => {
    const c = cases.find(x => x.id === caseId);
    const existing = (c?.ipDisputes ?? []) as IpDispute[];
    const dispute = existing.find(d => d.id === disputeId);
    const now2 = new Date().toISOString();
    void patchIpDispute(caseId, disputeId, {
      status: outcome,
      finalDetermination: outcome,
      finalDeterminationAt: now2,
      settlementPlatformNotifiedAt: now2,
      action: { code: 'IP_DISPUTE_RESOLVED', detail: { disputeId, outcome } },
    })
      .then(d => {
        appendIp(caseId, d);
        // Strike tracking — TERMINATED/REMOVED count toward the repeat-infringer
        // threshold. infringerStrikes lives on the case JSONB column, so the
        // existing updateCase / PATCH /cases path is the right call here.
        if ((outcome === 'TERMINATED' || outcome === 'REMOVED') && c) {
          const strikes = [
            ...((c.infringerStrikes || []) as Array<{ date: string }>),
            { date: now2, type: 'VALID_CLAIM', disputeId, details: `${dispute?.type} claim — ${outcome}` },
          ];
          const recentStrikes = strikes.filter(s => daysBetween(s.date, now2) <= 365);
          const strikeCode = recentStrikes.length >= STRIKE_THRESHOLDS.VALID_CLAIMS ? 'REPEAT_INFRINGER_STRIKE3'
            : recentStrikes.length >= 2 ? 'REPEAT_INFRINGER_STRIKE2'
            : 'REPEAT_INFRINGER_STRIKE1';
          updateCase(caseId, {
            infringerStrikes: strikes,
            action: { code: strikeCode, detail: { strikeNumber: recentStrikes.length, disputeId } },
          });
        }
      })
      .catch(err => showToast(`Failed to resolve claim: ${err instanceof Error ? err.message : String(err)}`));
  }, [cases, appendIp, showToast, updateCase]);

  return {
    createShaftDisputeForCase, notifyRegistrantShaft, respondShaftDispute, resolveShaftDispute,
    createIpDisputeForCase, notifyRegistrantIp, suspendIpContent, respondIpDispute, resolveIpDispute,
  };
}
